import { useAuthTokenContext } from '@/auth/KeycloakProvider';

export const AUTH_QUERY_KEYS = {
  session: ['auth', 'session'] as const,
  user: ['auth', 'user'] as const,
};

/**
 * Session-like object for backward compatibility with components
 * that expect Supabase-style session structure.
 */
type SessionLike = {
  user: {
    id: string;
    email: string;
  };
} | null;

/**
 * User-like object for backward compatibility with components
 * that expect Supabase-style user structure.
 */
type UserLike = {
  id: string;
  email: string;
  isAdmin: boolean;
} | null;

/**
 * Hook to get auth session state from AuthTokenProvider.
 * Returns session-like object for backward compatibility with existing components.
 */
export const useAuthSession = () => {
  const context = useAuthTokenContext();

  if (context.tokenStatus === 'loading') {
    return {
      data: undefined as SessionLike | undefined,
      isLoading: true,
      isError: false,
      error: null,
    };
  }

  if (context.tokenStatus === 'invalid') {
    return {
      data: null as SessionLike,
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  // tokenStatus === 'valid'
  return {
    data: {
      user: {
        id: context.userID,
        email: context.email,
      },
    } as SessionLike,
    isLoading: false,
    isError: false,
    error: null,
  };
};

/**
 * Hook to get auth user state from AuthTokenProvider.
 * Returns user-like object for backward compatibility with existing components.
 */
export const useAuthUser = () => {
  const context = useAuthTokenContext();

  if (context.tokenStatus === 'loading') {
    return {
      data: undefined as UserLike | undefined,
      isLoading: true,
      isError: false,
      error: null,
    };
  }

  if (context.tokenStatus === 'invalid') {
    return {
      data: null as UserLike,
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  // tokenStatus === 'valid'
  return {
    data: {
      id: context.userID,
      email: context.email,
      isAdmin: context.isAdmin,
    } as UserLike,
    isLoading: false,
    isError: false,
    error: null,
  };
};
